import { Subject } from 'rxjs';
import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { InvoiceService } from '@app/core/services/invoice.service';
import { MultiSelectModel } from '@app/shared/models/multi-select.model';
import { TableActionIcon, TableRowAction } from '@app/shared/models/table-action-type.model';
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { TableColumnsConfigKey } from '@app/core/models/configuration/table-columns-config-key.model';
import { LateInvoiceModel } from '@app/core/models/invoices/invoice-late.model';
import { ReferentialItem } from '@app/core/models/shared/referential-item.model';
import { ConfigService } from '@app/core/config/config.service';
import { DropdownHelper } from '@app/shared/models/dropdown-helper.model';
import { SessionStorageService } from '@app/core/services/session-storage.service';
import * as _ from 'lodash';
import { LateInvoiceGroupModel } from '@app/core/models/invoices/invoice-late-group.model';

@Component({
  selector: 'app-invoice-list-late',
  templateUrl: './invoice-list-late.component.html',
  styleUrls: ['./invoice-list-late.component.scss']
})
export class InvoiceLateComponent implements OnInit, OnDestroy {

  invoicesGroups: LateInvoiceGroupModel[] = [];
  highlightedGroup: LateInvoiceGroupModel[] = [];
  invoices: LateInvoiceModel[] = [];
  tableActions: TableRowAction[] = [];
  delay: number;
  debounceTime = 500;
  filterStorageKey: string;

  private delayChanged: Subject<number> = new Subject<number>();
  protected unsubscribe$: Subject<boolean> = new Subject<boolean>();

  reverseFactoringSelectedItems: ReferentialItem[] = [];
  workingSituationSelectedItems: ReferentialItem[] = [];

  dropdownSettings: IDropdownSettings = {};
  dropdownList: ReferentialItem[] = [];

  configKeyGrps: string = TableColumnsConfigKey.INVOICELATEGRP;
  configKeyInvoices: string = TableColumnsConfigKey.INVOICELATEINVOICES;
  exportFileName: string;
  exportDetailFileName: string;
  exportDetailFileNameSuffix: string;

  title: string;
  panelOpenState: boolean = true;

  public onFiltersCleared: EventEmitter<void> = new EventEmitter<void>();
  public onExportExcelCaller: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private route: ActivatedRoute,
    private invoiceService: InvoiceService,
    private configService: ConfigService,
    private dropdownHelper: DropdownHelper,
    private multiSelectModel: MultiSelectModel,
    private sessionStorageService: SessionStorageService
  ) {
    this.title = "SUIVI DES FACTURES";
    this.exportFileName = 'Factures_En_Retard';
    this.exportDetailFileName = 'Factures_En_Retard';
  }

  ngOnInit(): void {

    this.delay = this.configService.environnement.lateInvoicesDefaultDelay;
    this.filterStorageKey = this.route.snapshot.data.searchFilterKey;

    this.tableActions.push(new TableRowAction(TableActionIcon.ZOOM, 'Voir le détail', this.onZoom));

    this.setUpDropdownLists();
    this.restoreFilters();

    this.route.data.subscribe(data => {
      this.invoicesGroups = data.groups;
    });

    this.delayChanged
      .pipe(takeUntil(this.unsubscribe$),
        debounceTime(this.debounceTime))
      .subscribe((delay: number) => {
        this.delay = delay;
        this.onItemSelect();
      });
  }

  inputChanged(delay: number) {
    this.delayChanged.next(delay);
  }

  onSelectAll(items: any, filter: string) {
    switch (filter) {
      case 'reverseFactoring':
        this.reverseFactoringSelectedItems = items;
        break;
      case 'workingSituation':
        this.workingSituationSelectedItems = items;
        break;
    }
    this.onItemSelect();
  }

  onZoom = (data: LateInvoiceGroupModel) => {
    this.exportDetailFileNameSuffix = data.GroupName;
    const selectedReverseFactoring = this.getDropdownValue(this.reverseFactoringSelectedItems);
    const selectedWorkingSituation = this.getDropdownValue(this.workingSituationSelectedItems);
    this.invoiceService.getLateInvoices(data.GroupId, this.delay, selectedReverseFactoring, selectedWorkingSituation)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((invoices: LateInvoiceModel[]) => {
        this.highlightedGroup = [data];
        this.invoices = invoices;
      });
  }

  onItemSelect(): void {
    const selectedReverseFactoring = this.getDropdownValue(this.reverseFactoringSelectedItems);
    const selectedWorkingSituation = this.getDropdownValue(this.workingSituationSelectedItems);
    this.invoiceService.getLateGroupsInvoices(this.delay, selectedReverseFactoring, selectedWorkingSituation)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((groups: LateInvoiceGroupModel[]) => {
        this.highlightedGroup = [];
        this.invoices = [];
        this.invoicesGroups = groups;
        this.storeSearchFilters();
      });
  }

  get getDetailLateInvoiceExportFileName(): string {
    return `${this.exportDetailFileName}_[${this.exportDetailFileNameSuffix}]`;
  }

  private getDropdownValue(selectedItems: ReferentialItem[]): boolean | null {
    if (_.isNil(selectedItems) || !selectedItems.length) {
      return null;
    }

    return _.head(selectedItems).Id === 1;
  }

  private setUpDropdownLists(): void {
    this.dropdownList = this.dropdownHelper.getYesOrNoDropdownList();
    this.dropdownSettings = this.multiSelectModel.getSettings(null, null, true);
  }

  private storeSearchFilters(): void {
    this.sessionStorageService.store(this.filterStorageKey, {
      ReverseFactoringSelectedItems: this.reverseFactoringSelectedItems,
      WorkingSituationSelectedItems: this.workingSituationSelectedItems,
      Delay: this.delay
    });
  }

  private restoreFilters(): void {
    const searchFilter = this.sessionStorageService.get<any>(this.filterStorageKey);
    if (_.isNil(searchFilter)) {
      return;
    }

    if (!_.isNil(searchFilter.ReverseFactoringSelectedItems)) {
      this.reverseFactoringSelectedItems = [...searchFilter.ReverseFactoringSelectedItems];
    }

    if (!_.isNil(searchFilter.WorkingSituationSelectedItems)) {
      this.workingSituationSelectedItems = [...searchFilter.WorkingSituationSelectedItems];
    }

    this.delay = searchFilter.Delay;
  }

  onClearFilters = (): void => {
    this.reverseFactoringSelectedItems = [];
    this.workingSituationSelectedItems = [];
    this.delay = 5;
    this.onItemSelect();
  }

  ngOnDestroy() {
    this.unsubscribe$.next(true);
  }

  exportAsExcel() : void {
    this.onExportExcelCaller.next();
  }

  onShowFilter(panelvalue:boolean) {
    this.panelOpenState = panelvalue;
  }
}
